import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import styles from './TherapiesExpandableTable.module.scss';

// Cip za rezervacije u redu terapije:
//  - unapred rezervisano: row.reservation (sopstvena forward rezervacija, pending)
//  - row.reservationActivation = rezervacija sa prethodne terapije koja se ovde
//    moze primeniti (cip "Primeni rezervaciju")
// Aktuelno rezervisano (secondaryStatus === 'reserved') se NE prikazuje ovde -
// to ima na stranici Rezervisane Th.

const ForwardIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M13 5l7 7-7 7" />
    <path d="M4 5l7 7-7 7" />
  </svg>
);

const capitalize = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

// Srpska deklinacija reci "terapija" po broju.
const therapyWord = (count) => {
  const abs = Math.abs(Number(count) || 0) % 100;
  const last = abs % 10;
  if (abs >= 11 && abs <= 14) return 'terapija';
  if (last === 1) return 'terapija';
  if (last >= 2 && last <= 4) return 'terapije';
  return 'terapija';
};

// Period unapred rezervacije. Koristi eksplicitni opseg sa beka (startDate/endDate)
// ako postoji; u suprotnom pada na broj meseci (count) kao "N mes.".
const getAdvancePeriodLabel = (reservation) => {
  const count = Number(reservation?.count) || 0;

  const start = reservation?.startDate ? dayjs(reservation.startDate) : null;
  const end = reservation?.endDate ? dayjs(reservation.endDate) : null;

  if (start?.isValid() && end?.isValid()) {
    const formatMonth = (value) => capitalize(value.format('MMM YYYY'));
    return start.isSame(end, 'month')
      ? formatMonth(start)
      : `${formatMonth(start)} – ${formatMonth(end)}`;
  }

  if (reservation?.periodLabel) return reservation.periodLabel;

  return count ? `${count} mes.` : null;
};

const advanceReservationShape = PropTypes.shape({
  _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  count: PropTypes.number,
  quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  medication: PropTypes.string,
  note: PropTypes.string,
  label: PropTypes.string,
  therapyName: PropTypes.string,
  startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  periodLabel: PropTypes.string,
});

// Jedan "Unapred" cip - zadrzan zbog kompatibilnosti (npr. redovi terapija).
// Za stranicu Rezervisane Th. koristi se prominentni AdvanceReservationsPanel.
export const AdvanceReservationPill = ({ reservation }) => {
  if (!reservation) return null;

  const label =
    reservation.label ||
    [reservation.count, reservation.medication].filter(Boolean).join(' ');

  const text = reservation.medication
    ? `Unapred: ${reservation.count} × ${reservation.medication}`
    : `Unapred: ${label}`;

  const tooltip = [
    `Unapred rezervisano (buduće terapije, do 3 meseca): ${label}`,
    reservation.therapyName,
    reservation.note,
  ]
    .filter(Boolean)
    .join(' — ');

  return (
    <span
      className={`${styles.reservationPill} ${styles['reservationPill--advance']}`}
      title={tooltip}
    >
      <ForwardIcon />
      <span className={styles.reservationPill__text}>{text}</span>
    </span>
  );
};

AdvanceReservationPill.propTypes = {
  reservation: advanceReservationShape,
};

// Prominentni panel za unapred rezervisane terapije jednog pacijenta.
// Jasno vidljiv, sa bitnim podacima: lek, količina (ako je ima), broj
// rezervisanih terapija i period. Prikazuje se iznad aktuelno rezervisanih
// aktivnosti u istom redu.
export const AdvanceReservationsPanel = ({ reservations }) => {
  const list = Array.isArray(reservations) ? reservations.filter(Boolean) : [];

  if (!list.length) return null;

  // Ukupan broj unapred rezervisanih terapija za pacijenta (zbir count-ova).
  const totalCount = list.reduce(
    (sum, reservation) => sum + (Number(reservation?.count) || 0),
    0
  );

  return (
    <div className={styles.advancePanel}>
      <div className={styles.advancePanel__header}>
        <ForwardIcon />
        <span className={styles.advancePanel__title}>Unapred rezervisano</span>
        {totalCount > 0 && (
          <span
            className={styles.advancePanel__count}
            title={`Ukupno unapred rezervisano: ${totalCount} ${therapyWord(
              totalCount
            )}`}
          >
            {totalCount} {therapyWord(totalCount)}
          </span>
        )}
      </div>
      <div className={styles.advancePanel__list}>
        {list.map((reservation, index) => {
          const count = Number(reservation?.count) || 0;
          const medication =
            reservation?.medication || reservation?.label || '-';
          const quantity = reservation?.quantity;
          const period = getAdvancePeriodLabel(reservation);

          const meta = [
            quantity ? `${quantity} kom` : null,
            count ? `${count} ${therapyWord(count)}` : null,
            period,
          ].filter(Boolean);

          return (
            <div
              className={styles.advancePanel__item}
              key={reservation?._id || `advance-${index}`}
              title={[medication, reservation?.therapyName, reservation?.note]
                .filter(Boolean)
                .join(' — ')}
            >
              <span className={styles.advancePanel__med}>{medication}</span>
              {meta.length > 0 && (
                <span className={styles.advancePanel__meta}>
                  {meta.join(' · ')}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

AdvanceReservationsPanel.propTypes = {
  reservations: PropTypes.arrayOf(advanceReservationShape),
};

const ReservationBadges = ({ row }) => {
  // "Unapred" oznaka rezervacije se vise ne prikazuje na redu Terapija -
  // rezervacije su na stranici Rezervisane Th. Ostaje samo hint da se moze
  // primeniti rezervacija sa prethodne terapije (i to samo kad red nema
  // sopstvenu forward rezervaciju, kao i ranije).
  const activationPending = !row?.reservation && !!row?.reservationActivation;

  if (!activationPending) return null;

  return (
    <div className={styles.reservationBadges}>
      <span
        className={`${styles.reservationPill} ${styles['reservationPill--advancePending']}`}
        title="Rezervacija sa prethodne terapije — može se primeniti"
      >
        <ForwardIcon />
        <span className={styles.reservationPill__text}>
          Primeni rezervaciju
        </span>
      </span>
    </div>
  );
};

ReservationBadges.propTypes = {
  row: PropTypes.shape({
    occurrences: PropTypes.arrayOf(PropTypes.object),
    medications: PropTypes.arrayOf(PropTypes.string),
    reservation: advanceReservationShape,
    reservationActivation: PropTypes.object,
  }),
};

export default ReservationBadges;
